import { IconButton } from '@mui/material';
import { useRef, useState } from 'react';
import { s } from '@/theme/tokens';
import { Box, Cancel, CloseIcon, InputAdornment, SearchIcon, TextField } from '@/ui/primitives';
import { filterInputSx } from './filterControlStyles';
import type { TextFieldProps } from '@/ui/primitives';
import type { FocusEvent, ReactElement } from 'react';

type SearchFilterFieldProps = Omit<TextFieldProps, 'value' | 'label'> & {
  value: string;
  label?: TextFieldProps['label'];
  inputAriaLabel?: string;
  compact?: boolean;
  controlHeight?: number;
  leadingSearchAdornment?: boolean;
  trailingSearchAdornment?: boolean;
  filledClearIcon?: boolean;
};

export const SearchFilterField = ({
  value,
  label,
  inputAriaLabel,
  compact = false,
  controlHeight,
  leadingSearchAdornment = false,
  trailingSearchAdornment = true,
  filledClearIcon = false,
  slotProps,
  sx,
  ...rest
}: SearchFilterFieldProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const resolvedHeight = controlHeight ?? 32;
  const resolvedPaddingY = compact ? s('none') : s('xxs');
  const resolvedPaddingLeft = leadingSearchAdornment
    ? `calc(${s('sm')} * -1)`
    : `calc(${s('md')} + ${s('xs')})`;
  const showClear = value.length > 0;
  const hasValue = value.length > 0;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const maxCollapsedChars = 64;
  const displayValue =
    !isInputFocused && value.length > maxCollapsedChars
      ? `${value.slice(0, maxCollapsedChars - 1)}...`
      : value;
  const resolvedPaddingRight = showClear ? `calc(${s('md')} + ${s('xxs')})` : s('lg');
  const adornmentSx = {
    mr: `calc(${s('sm')} * -1.5)`,
    pl: s('none'),
    ml: s('none'),
    gap: s('none'),
    '&.MuiInputAdornment-positionEnd': {
      marginLeft: `-${s('sm')}`,
      marginRight: `calc(${s('sm')} * -1) !important`,
    },
  } as const;
  const startAdornment = leadingSearchAdornment ? (
    <InputAdornment
      position="start"
      sx={{
        ml: `calc(${s('md')} * -1)`,
        mr: s('xs'),
      }}
    >
      <SearchIcon fontSize="small" />
    </InputAdornment>
  ) : null;
  const endAdornment = (
    <InputAdornment position="end" sx={adornmentSx}>
      {showClear ? (
        <IconButton
          aria-label="Clear"
          title="Clear"
          edge="end"
          size="small"
          className="search-clear-button"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            const inputElement = inputRef.current;
            if (!inputElement) return;
            Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(
              inputElement,
              '',
            );
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.focus();
          }}
          sx={{
            opacity: 1,
            pointerEvents: 'auto',
            p: s('xxs'),
            width: 'auto',
            height: 'auto',
            ml: `-${s('xxs')}`,
            mr: `calc(${s('sm')} * -1) !important`,
            color: filledClearIcon ? 'common.white' : 'action.active',
          }}
        >
          {filledClearIcon ? <Cancel fontSize="small" /> : <CloseIcon fontSize="small" />}
        </IconButton>
      ) : null}
      {trailingSearchAdornment ? (
        <Box sx={{ fontSize: '0.95rem', transform: 'translateY(2px)' }} aria-hidden="true">
          <SearchIcon fontSize="inherit" />
        </Box>
      ) : null}
    </InputAdornment>
  );
  const baseSlotProps = typeof slotProps === 'function' ? {} : (slotProps ?? {});
  const mergedSlotProps: TextFieldProps['slotProps'] = {
    ...baseSlotProps,
    input: (ownerState) => {
      const resolved =
        typeof baseSlotProps.input === 'function'
          ? baseSlotProps.input(ownerState)
          : baseSlotProps.input;
      return {
        ...resolved,
        startAdornment: resolved?.startAdornment ?? startAdornment,
        endAdornment: resolved?.endAdornment ?? endAdornment,
      };
    },
    htmlInput: (ownerState) => {
      const resolved =
        typeof baseSlotProps.htmlInput === 'function'
          ? baseSlotProps.htmlInput(ownerState)
          : baseSlotProps.htmlInput;
      const resolvedLabel =
        resolved?.['aria-label'] ??
        inputAriaLabel ??
        (typeof label === 'string' ? label : undefined);
      return {
        ...resolved,
        ref: (instance: HTMLInputElement | null) => {
          if (typeof resolved?.ref === 'function') {
            resolved.ref(instance);
          } else if (resolved?.ref && typeof resolved.ref === 'object') {
            resolved.ref.current = instance;
          }
          inputRef.current = instance;
        },
        style: {
          ...resolved?.style,
          paddingLeft: resolvedPaddingLeft,
          paddingInlineStart: resolvedPaddingLeft,
        },
        onFocus: (event: FocusEvent<HTMLInputElement>) => {
          resolved?.onFocus?.(event);
          setIsInputFocused(true);
        },
        onBlur: (event: FocusEvent<HTMLInputElement>) => {
          resolved?.onBlur?.(event);
          setIsInputFocused(false);
        },
        'aria-label': resolvedLabel,
      };
    },
  };
  const heightSx = {
    ...(hasValue
      ? {
          '&& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main !important',
          },
          '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main !important',
          },
          '&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main !important',
          },
        }
      : {}),
    '& .MuiInputBase-root': {
      minHeight: `${resolvedHeight}px !important`,
      height: `${resolvedHeight}px !important`,
      maxHeight: `${resolvedHeight}px !important`,
      alignItems: 'center',
      minWidth: 0,
      overflow: 'hidden',
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      minHeight: `${resolvedHeight}px !important`,
      height: `${resolvedHeight}px !important`,
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
      minHeight: `${resolvedHeight}px !important`,
      height: `${resolvedHeight}px !important`,
    },
    '& .MuiOutlinedInput-root': {
      minHeight: `${resolvedHeight}px !important`,
      height: `${resolvedHeight}px !important`,
    },
    '& .MuiInputBase-input': {
      minHeight: `${resolvedHeight}px !important`,
      height: `${resolvedHeight}px !important`,
      minWidth: '0 !important',
      width: '100%',
      flex: '1 1 auto',
      paddingLeft: `${resolvedPaddingLeft} !important`,
      paddingInlineStart: `${resolvedPaddingLeft} !important`,
      paddingTop: `${resolvedPaddingY} !important`,
      paddingBottom: `${resolvedPaddingY} !important`,
      paddingRight: `${resolvedPaddingRight} !important`,
      lineHeight: 1,
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiOutlinedInput-input': {
      minHeight: `${resolvedHeight}px !important`,
      height: `${resolvedHeight}px !important`,
      minWidth: '0 !important',
      width: '100%',
      flex: '1 1 auto',
      paddingLeft: `${resolvedPaddingLeft} !important`,
      paddingInlineStart: `${resolvedPaddingLeft} !important`,
      paddingTop: `${resolvedPaddingY} !important`,
      paddingBottom: `${resolvedPaddingY} !important`,
      paddingRight: `${resolvedPaddingRight} !important`,
      lineHeight: 1,
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-input.MuiInputBase-input': {
      paddingLeft: `${resolvedPaddingLeft} !important`,
      paddingInlineStart: `${resolvedPaddingLeft} !important`,
    },
  } as const;
  const typographySx: TextFieldProps['sx'] = (theme) => ({
    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
      ...theme.typography.body1,
      fontSize: '15px',
    },
    '& .MuiInputBase-input, & .MuiOutlinedInput-input, & input': {
      ...theme.typography.body1,
      fontSize: '15px',
    },
  });

  return (
    <TextField
      {...rest}
      label={label}
      value={displayValue}
      focused={hasValue || rest.focused === true}
      size="small"
      inputHeight={resolvedHeight}
      slotProps={mergedSlotProps}
      sx={[filterInputSx, typographySx, heightSx, sx].filter(Boolean) as TextFieldProps['sx']}
    />
  );
};
